import type { ReactElement, ReactNode } from 'react';

import { MyNav } from '@/widgets/my-nav';
import { useSignOut } from '@/features/sign-out';
import { MemberAvatar, memberNickname, useSession } from '@/entities/session';

/**
 * 마이페이지 공통 껍데기 — 왼쪽 메뉴 + 오른쪽 본문.
 *
 * 탭을 위에 눕히던 것을 옆으로 세웠다. 탭은 항목이 늘어나면 가로로 넘치고, 스크롤을 내리면
 * 사라져서 지금 어디에 있는지를 잃는다. 옆에 두면 둘 다 없다.
 *
 * 누구로 들어와 있는지를 메뉴 맨 위에 둔다 — 화면마다 제목 아래에 이메일을 또 적지 않아도
 * 되고, 계정을 잘못 잡고 설정을 바꾸는 일이 줄어든다.
 *
 * 좁은 화면에서는 위아래로 쌓인다. 세로 메뉴를 그대로 두면 본문이 화면 밖으로 밀린다.
 */
export function MyShell({
  title,
  description,
  hasPhysicalOrder = true,
  children,
}: {
  title: string;
  description?: string;
  /** 실물 주문이 있어야 배송 조회 탭이 뜬다 */
  hasPhysicalOrder?: boolean;
  children: ReactNode;
}): ReactElement {
  const member = useSession((state) => state.member);
  const signOut = useSignOut();

  return (
    <div className="u-container grid gap-8 py-10 lg:grid-cols-[13rem_minmax(0,1fr)] lg:items-start lg:gap-12">
      <aside className="flex flex-col gap-5 lg:sticky lg:top-24">
        {member ? (
          <div className="flex items-center gap-3">
            <MemberAvatar />
            <div className="flex min-w-0 flex-col">
              <p className="truncate text-[14px] font-medium">
                {memberNickname(member) || member.email.split('@')[0]}
              </p>
              <p className="truncate text-[12px] text-muted-foreground">{member.email}</p>
            </div>
          </div>
        ) : null}

        <MyNav hasPhysicalOrder={hasPhysicalOrder} />

        {member ? (
          <button
            type="button"
            onClick={signOut}
            className="w-fit px-3 text-[13px] text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            로그아웃
          </button>
        ) : null}
      </aside>

      <div className="flex min-w-0 flex-col gap-6">
        <header className="flex flex-col gap-2">
          <h1 className="text-[clamp(1.375rem,1.2rem+0.8vw,1.75rem)] font-bold tracking-[-0.04em]">
            {title}
          </h1>
          {description ? <p className="text-[14px] text-muted-foreground">{description}</p> : null}
        </header>
        {children}
      </div>
    </div>
  );
}

export function MyLoading(): ReactElement {
  return <div aria-busy="true" className="h-72 animate-pulse rounded-lg bg-secondary" />;
}

export function MyError({ label }: { label: string }): ReactElement {
  return (
    <p className="rounded-lg border border-border border-dashed p-12 text-center text-[15px]">
      {label}
    </p>
  );
}
